/*
 * COPYRIGHT Motorola Solutions, INC.
 * ALL RIGHTS RESERVED.
 * MOTOROLA SOLUTIONS CONFIDENTIAL RESTRICTED
 */

import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { AsteriskPasswordRevokedEvent } from 'CalltakingCoreApi';
import {
    fetchAudibleAlerts,
    fetchCtcConfiguration,
    fetchSkipperConfiguration,
    hasInitialized,
    sipPeerPasswordRevokedEvent,
    uiLogStreamingUpdate
} from '../+state/configuration.actions';
import { selectConfigurationEffectInitialized, selectHasInitialized } from '../+state/configuration.selectors';
import { filter, take } from 'rxjs/operators';
import { AudibleAlerts } from '../../call/model/audible-alert';
import { ClusterUrlProviderService } from '../../core/services/cluster-url-provider.service';
import { ConnectionState } from '../../core/model/connectionState';
import { selectDirectoryEffectInitialized } from '../../directory/+state/directory.selectors';
import { fetchDirectory } from '../../directory/+state/directory.actions';
import { concatLatestFrom } from '@ngrx/operators';
import { StompBaseConsumer } from '../../core/abstract/stomp-base-consumer';
import { StompManagerService } from '../../core/services/stomp-manager/stomp-manager.service';
import { Topic } from '../../user/model/multiple-subscriptions';
import { selectConnectionStatus } from '../../call/+state/call.selectors';
import { UILogStreamingConfig } from '../model/ui-log-streaming-config';
import { selectPositionId } from '../../user/+state/user.selectors';
import { EnvironmentService } from '../../core/services/environment.service';

@Injectable({
    providedIn: 'root'
})
export class ConfigurationService extends StompBaseConsumer implements OnDestroy {
    private readonly CTC_VERSION_URL = '/api/config/v1/version';
    private readonly CTC_PEER_URL = '/api/config/v1/asterisk-peer';
    private readonly CTC_PEER_PASSWORD_URL = '/api/config/v1/asterisk-password';

    constructor(
        private http: HttpClient,
        private clusterUrlProviderService: ClusterUrlProviderService,
        private envService: EnvironmentService,
        protected store: Store,
        protected stompManager: StompManagerService
    ) {
        super(stompManager, store);
        this.store.select(selectDirectoryEffectInitialized).pipe(filter((init) => init))
            .subscribe(() => this.store.dispatch(fetchDirectory()));

        this.store
            .select(selectConfigurationEffectInitialized)
            .pipe(filter((init) => init))
            .subscribe(() => {
                this.store.dispatch(fetchCtcConfiguration());
                this.store.dispatch(fetchSkipperConfiguration());
                this.store.dispatch(fetchAudibleAlerts());
            });

        this.store
            .select(selectConnectionStatus)
            .pipe(concatLatestFrom(() => this.store.select(selectHasInitialized)))
            .subscribe(([status, initialized]) => {
                if ((!initialized && status === ConnectionState.PARTIAL) || status === ConnectionState.FULL) {
                    this.store.dispatch(hasInitialized());
                }
            });
    }

    protected handlePeerPasswordRevokedEventMessage(message: AsteriskPasswordRevokedEvent) {
        this.handlePeerPasswordRevokedEvent(message);
    }

    protected handleUiLoggingUpdateMessage(config: UILogStreamingConfig) {
        console.info('[UI Logging Config] Received WebSocket update', config);

        this.store
            .select(selectPositionId)
            .pipe(take(1))
            .subscribe((position) => {
                if (position === config.consolePosition) {
                    this.store.dispatch(uiLogStreamingUpdate({ config }));
                    return;
                }

                console.debug(
                    `[UI Logging Config] Ignoring WebSocket update for position=${config.consolePosition}; current position=${position}`
                );
            });
    }

    protected getTopics(): Topic[] {
        if (this.envService.environment?.featureFlags?.uiLogStreaming) {
            return [
                Topic.PEER_PASSWORD_REVOKED,
                Topic.UI_LOG_STREAMING_NEW,
                Topic.UI_LOG_STREAMING_EDIT,
                Topic.UI_LOG_STREAMING_UPDATE,
                Topic.UI_LOG_STREAMING_DELETE,
            ];
        }
        return [Topic.PEER_PASSWORD_REVOKED];
    }

    protected getTopicHandlers(): { [p in Topic]?: (message: any) => void } {
        return {
            [Topic.PEER_PASSWORD_REVOKED]: this.handlePeerPasswordRevokedEventMessage.bind(this),
            [Topic.UI_LOG_STREAMING_NEW]: this.handleUiLoggingUpdateMessage.bind(this),
            [Topic.UI_LOG_STREAMING_EDIT]: this.handleUiLoggingUpdateMessage.bind(this),
            [Topic.UI_LOG_STREAMING_UPDATE]: this.handleUiLoggingUpdateMessage.bind(this),
            [Topic.UI_LOG_STREAMING_DELETE]: this.handleUiLoggingUpdateMessage.bind(this),
        };
    }

    handleSubscriptionInit(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel || ''} Configuration topic ${topic} subscription success: ${receiptId}`);
    }
    handleSubscriptionEnd(topic: string, receiptId: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel || ''} Configuration topic ${topic} subscription end: ${receiptId}`);
    }
    handleStompError(topic: string, receiptId: string, error: string, clusterName?: string, clusterLabel?: string): void {
        console.info(`${clusterLabel || ''} Configuration topic ${topic} subscription failed=${error} for receiptId=${receiptId}`);
    }

    private handlePeerPasswordRevokedEvent(event: AsteriskPasswordRevokedEvent) {
        this.store.dispatch(sipPeerPasswordRevokedEvent({ event: event }));
    }

    public requestCtcPeerConfiguration(clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, this.CTC_PEER_URL);
        return this.http.get(url, { observe: 'body', responseType: 'text' });
    }

    public requestCtcPeerPassword(clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, this.CTC_PEER_PASSWORD_URL);
        return this.http.get(url, { observe: 'body', responseType: 'text' });
    }

    public requestCtcVersion(clusterName: string) {
        const url = this.clusterUrlProviderService.getClusterSpecificUrl(clusterName, this.CTC_VERSION_URL);
        return this.http.get(url, { observe: 'body', responseType: 'text' });
    }

    public requestAlerts() {
        return this.http.get<AudibleAlerts>(`assets/data/call-alerts.json`, { observe: 'body', responseType: 'json' });
    }
}
